import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InlineSelfieCamera.module.css';

interface Props {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
}

type Phase = 'init' | 'live' | 'denied' | 'unsupported';

type FacingMode = 'user' | 'environment';

interface CapabilitiesWithTorch extends MediaTrackCapabilities {
  torch?: boolean;
}

interface ConstraintsWithTorch extends MediaTrackConstraintSet {
  torch?: boolean;
}

export function InlineSelfieCamera({ onCapture, onCancel }: Props): JSX.Element {
  const { t } = useTranslation('flows');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<Phase>('init');
  const [facing, setFacing] = useState<FacingMode>('user');
  const [mirror, setMirror] = useState(true);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const stopStream = useCallback((): void => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async (next: FacingMode): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('unsupported');
      return;
    }
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: next, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        void videoRef.current.play();
      }
      const track = stream.getVideoTracks()[0];
      const caps = (track?.getCapabilities?.() ?? {}) as CapabilitiesWithTorch;
      setTorchSupported(Boolean(caps.torch));
      setTorchOn(false);
      setPhase('live');
    } catch {
      setPhase('denied');
    }
  }, [stopStream]);

  useEffect(() => {
    void startCamera(facing);
    return () => {
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = useCallback((): void => {
    const next: FacingMode = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    void startCamera(next);
  }, [facing, startCamera]);

  const toggleMirror = useCallback((): void => {
    setMirror((m) => !m);
  }, []);

  const toggleTorch = useCallback(async (): Promise<void> => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const nextOn = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: nextOn } as ConstraintsWithTorch] });
      setTorchOn(nextOn);
    } catch {
      setTorchSupported(false);
    }
  }, [torchOn]);

  const capture = useCallback((): void => {
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sw = v.videoWidth;
    const sh = v.videoHeight;
    const side = Math.min(sw, sh);
    const sx = (sw - side) / 2;
    const sy = (sh - side) / 2;
    if (mirror) {
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, sx, sy, side, side, 0, 0, size, size);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stopStream();
        onCapture(blob);
      },
      'image/jpeg',
      0.92,
    );
  }, [mirror, onCapture, stopStream]);

  const cancel = useCallback((): void => {
    stopStream();
    onCancel();
  }, [stopStream, onCancel]);

  const isLive = phase === 'live';
  const isDenied = phase === 'denied' || phase === 'unsupported';

  return (
    <div className={styles.root} data-testid="inline-selfie-camera">
      <button
        type="button"
        className={styles.closeBtn}
        onClick={cancel}
        aria-label={t('avatarFlow.camera.cancel')}
        data-testid="inline-camera-cancel"
      >
        ✕
      </button>

      <div className={styles.viewfinder}>
        {isDenied ? (
          <div className={styles.deniedBox}>
            <p>{t('avatarFlow.camera.permissionDenied')}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            className={styles.video}
            playsInline
            muted
            style={{ transform: mirror ? 'scaleX(-1)' : 'none' }}
          />
        )}

      </div>

      {!isDenied && (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={flip}
            disabled={!isLive}
            aria-label={t('avatarFlow.camera.flip')}
            data-testid="inline-camera-flip"
          >
            <span aria-hidden="true">⇆</span>
            <span className={styles.toolLabel}>{t('avatarFlow.camera.flip')}</span>
          </button>

          <button
            type="button"
            className={`${styles.toolBtn} ${mirror ? styles.toolBtnActive : ''}`}
            onClick={toggleMirror}
            disabled={!isLive}
            aria-pressed={mirror}
            aria-label={t('avatarFlow.camera.mirror')}
            data-testid="inline-camera-mirror"
          >
            <span aria-hidden="true">⇋</span>
            <span className={styles.toolLabel}>{t('avatarFlow.camera.mirror')}</span>
          </button>

          {torchSupported && (
            <button
              type="button"
              className={`${styles.toolBtn} ${torchOn ? styles.toolBtnActive : ''}`}
              onClick={() => void toggleTorch()}
              disabled={!isLive}
              aria-pressed={torchOn}
              aria-label={t('avatarFlow.camera.torch')}
              data-testid="inline-camera-torch"
            >
              <span aria-hidden="true">⚡</span>
              <span className={styles.toolLabel}>{t('avatarFlow.camera.torch')}</span>
            </button>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.captureBtn}
          onClick={capture}
          disabled={!isLive}
          aria-label={t('avatarFlow.camera.capture')}
          data-testid="inline-camera-capture"
        >
          <span className={styles.captureRing} aria-hidden="true" />
          <span className={styles.captureDot} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
